/**
 * Accueil POP! — créer une salle ou en rejoindre une par son code.
 *
 * Le champ de code force les MAJUSCULES, est limité à ROOM_CODE_LENGTH, et
 * normalise à la saisie : coller « ab-cd12 » ou « ab cd 12 » donne « ABCD12 ».
 */
import { useState } from 'react';
import type { FormEvent } from 'react';
import { ROOM_CODE_LENGTH, pseudoSchema } from '@pop/contracts';
import { ApiRequestError, createRoom, joinRoom } from '../api.js';

interface HomeProps {
  readonly onEnter: (code: string, playerId: string) => void;
}

type Busy = 'create' | 'join' | null;

/** Majuscules, sans séparateurs, tronqué à la longueur d'un code de salle. */
function normalizeCode(value: string): string {
  return value
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, ROOM_CODE_LENGTH);
}

function pseudoError(pseudo: string): string | null {
  const checked = pseudoSchema.safeParse(pseudo);
  if (checked.success) {
    return null;
  }
  const issue = checked.error.issues[0];
  return typeof issue?.message === 'string' ? issue.message : 'pseudo invalide';
}

export function Home({ onEnter }: HomeProps) {
  const [createPseudo, setCreatePseudo] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [joinPseudo, setJoinPseudo] = useState('');
  const [busy, setBusy] = useState<Busy>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleCreate(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (busy !== null) {
      return;
    }
    const invalid = pseudoError(createPseudo);
    if (invalid !== null) {
      setError(invalid);
      return;
    }
    setBusy('create');
    setError(null);
    try {
      const created = await createRoom(createPseudo.trim());
      onEnter(created.code, created.playerId);
    } catch (unknownError: unknown) {
      setError(
        unknownError instanceof ApiRequestError
          ? unknownError.message
          : 'impossible de créer la salle',
      );
      setBusy(null);
    }
  }

  async function handleJoin(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (busy !== null) {
      return;
    }
    if (joinCode.length !== ROOM_CODE_LENGTH) {
      setError(`code incomplet — ${ROOM_CODE_LENGTH} caractères`);
      return;
    }
    const invalid = pseudoError(joinPseudo);
    if (invalid !== null) {
      setError(invalid);
      return;
    }
    setBusy('join');
    setError(null);
    try {
      const joined = await joinRoom(joinCode, joinPseudo.trim());
      onEnter(joinCode, joined.playerId);
    } catch (unknownError: unknown) {
      setError(
        unknownError instanceof ApiRequestError
          ? unknownError.message
          : 'impossible de rejoindre la salle',
      );
      setBusy(null);
    }
  }

  return (
    <div className="home">
      <header className="hero">
        <h1 className="hero-title">POP!</h1>
        <p className="hero-subtitle">Le jeu de soirée qui tient dans ta poche.</p>
      </header>

      {error !== null && (
        <p className="alert alert-error" role="alert">
          {error}
        </p>
      )}

      <section className="card" aria-labelledby="create-title">
        <h2 id="create-title" className="card-title">
          Créer une partie
        </h2>
        <form onSubmit={handleCreate}>
          <label className="field" htmlFor="create-pseudo">
            <span className="field-label">Ton pseudo</span>
            <input
              id="create-pseudo"
              className="input"
              type="text"
              autoComplete="nickname"
              maxLength={20}
              placeholder="Ex. Léo"
              value={createPseudo}
              onChange={(event) => {
                setCreatePseudo(event.target.value);
              }}
            />
          </label>
          <button className="btn btn-primary" type="submit" disabled={busy !== null}>
            {busy === 'create' ? 'Création…' : 'Créer une salle'}
          </button>
        </form>
      </section>

      <section className="card" aria-labelledby="join-title">
        <h2 id="join-title" className="card-title">
          Rejoindre une partie
        </h2>
        <form onSubmit={handleJoin}>
          <label className="field" htmlFor="join-code">
            <span className="field-label">Code de la salle</span>
            <input
              id="join-code"
              className="input input-code"
              type="text"
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              inputMode="text"
              placeholder="ABCDEF"
              // PAS de `maxLength` ici, volontairement.
              //
              // L'attribut tronque la saisie BRUTE, avant que `normalizeCode`
              // ne s'exécute. Coller « ab-cd 12 » donnerait « ab-cd » puis
              // « ABCD » — deux caractères perdus, sans aucun message. La
              // longueur est déjà garantie par le `slice` de la normalisation.
              value={joinCode}
              onChange={(event) => {
                setJoinCode(normalizeCode(event.target.value));
              }}
            />
          </label>
          <label className="field" htmlFor="join-pseudo">
            <span className="field-label">Ton pseudo</span>
            <input
              id="join-pseudo"
              className="input"
              type="text"
              autoComplete="nickname"
              maxLength={20}
              placeholder="Ex. Mia"
              value={joinPseudo}
              onChange={(event) => {
                setJoinPseudo(event.target.value);
              }}
            />
          </label>
          <button className="btn btn-secondary" type="submit" disabled={busy !== null}>
            {busy === 'join' ? 'Connexion…' : 'Rejoindre'}
          </button>
        </form>
      </section>
    </div>
  );
}
